import React, { useState, useEffect, useCallback } from 'react';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle, Clock, Calendar as CalendarIcon, CheckCircle2, Loader2, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * BookingSlotSelector - Interactive slot picker with Smart Overlap Blocking
 * 
 * Features:
 * - Calendar date picker with disabled non-business days
 * - Time slot grid with availability status
 * - Variant duration support (slot availability changes based on selected duration)
 * - Real-time availability checking
 * - Shows remaining capacity for concurrent sessions
 * 
 * Usage:
 * <BookingSlotSelector
 *   listingId="service-123"
 *   variants={[{name: "30 min", duration: 30}, {name: "60 min", duration: 60}]}
 *   onSlotSelect={(date, time, duration) => handleSlotSelect(date, time, duration)}
 * />
 */
const BookingSlotSelector = ({
  listingId,
  variants = [],
  defaultDuration = 60,
  onSlotSelect,
  onValidationResult,
  disabled = false,
  className = ""
}) => {
  const { apiCall } = useAuth();
  
  // State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [daySlots, setDaySlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);
  
  // Extract duration variants from variant data
  const durationVariants = variants.filter(v => {
    // Check if this is a time-based variant (Duration type)
    return v.name?.toLowerCase().includes('duration') || 
           v.options?.some(opt => /\d+\s*(min|hour|hr)/i.test(opt.name));
  });
  
  // Get duration options from variants
  const getDurationOptions = () => {
    const options = [];
    durationVariants.forEach(variant => {
      variant.options?.forEach(opt => {
        const match = opt.name?.match(/(\d+)\s*(min|hour|hr)?/i);
        if (match) {
          const value = parseInt(match[1]);
          const unit = match[2]?.toLowerCase() || 'min';
          const minutes = unit.includes('hour') || unit === 'hr' ? value * 60 : value;
          options.push({
            label: opt.name,
            value: minutes,
            priceImpact: opt.price_impact || 0
          });
        }
      });
    });
    
    // If no duration variants, use default
    if (options.length === 0) {
      options.push({
        label: `${defaultDuration} minutes`,
        value: defaultDuration,
        priceImpact: 0
      });
    }
    
    return options;
  };
  
  const durationOptions = getDurationOptions();
  const currentDuration = selectedVariant?.value || durationOptions[0]?.value || defaultDuration;
  
  // Fetch availability for selected date range
  const fetchAvailability = useCallback(async () => {
    if (!listingId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const startDate = formatDate(selectedDate);
      const endDate = formatDate(addDays(selectedDate, 6)); // Fetch week at a time
      
      const response = await apiCall('/api/booking-availability/slots', {
        method: 'POST',
        body: JSON.stringify({
          listing_id: listingId,
          start_date: startDate,
          end_date: endDate,
          variant_duration: currentDuration
        })
      });
      
      setAvailabilityData(response);
      
      // Set slots for selected date
      const selectedDateStr = formatDate(selectedDate);
      const dayData = response.days?.find(d => d.date === selectedDateStr);
      setDaySlots(dayData?.slots || []);
      
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError('Failed to load availability. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [listingId, selectedDate, currentDuration, apiCall]);
  
  // Fetch availability when date or duration changes
  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);
  
  // Update slots when selected date changes within cached data
  useEffect(() => {
    if (availabilityData) {
      const selectedDateStr = formatDate(selectedDate);
      const dayData = availabilityData.days?.find(d => d.date === selectedDateStr);
      setDaySlots(dayData?.slots || []);
    }
  }, [selectedDate, availabilityData]);
  
  // Validate selected slot before confirming
  const validateSlot = async () => {
    if (!selectedTime || !listingId) return;
    
    setValidating(true);
    
    try {
      const response = await apiCall('/api/booking-availability/validate', {
        method: 'POST',
        body: JSON.stringify({
          listing_id: listingId,
          date: formatDate(selectedDate),
          start_time: selectedTime,
          duration_minutes: currentDuration
        })
      });
      
      if (onValidationResult) {
        onValidationResult(response);
      }
      
      if (response.is_valid && onSlotSelect) {
        onSlotSelect({
          date: formatDate(selectedDate),
          time: selectedTime,
          duration: currentDuration,
          variant: selectedVariant
        });
      }
      
      return response;
    } catch (err) {
      console.error('Error validating slot:', err);
      setError('Failed to validate slot. Please try again.');
      return { is_valid: false, message: 'Validation failed' };
    } finally {
      setValidating(false);
    }
  };
  
  // Handle time slot click
  const handleTimeSelect = (slot) => {
    if (!slot.available) return;
    setSelectedTime(slot.start_time);
  };
  
  // Handle variant/duration change
  const handleDurationChange = (value) => {
    const option = durationOptions.find(o => o.value === parseInt(value));
    setSelectedVariant(option);
    setSelectedTime(null); // Reset time selection when duration changes
  };
  
  // Check if a date is a business day
  const isBusinessDay = (date) => {
    if (!availabilityData?.days) return true;
    const dateStr = formatDate(date);
    const dayData = availabilityData.days.find(d => d.date === dateStr);
    return dayData ? dayData.is_business_day && !dayData.is_holiday : true;
  };
  
  // Calendar disabled dates
  const disabledDays = (date) => {
    // Disable past dates
    if (date < new Date().setHours(0, 0, 0, 0)) return true;
    // Disable non-business days if we have data
    if (availabilityData) {
      return !isBusinessDay(date);
    }
    return false;
  };
  
  // Group slots by time period
  const groupSlotsByPeriod = (slots) => {
    const morning = slots.filter(s => {
      const hour = parseInt(s.start_time.split(':')[0]);
      return hour >= 6 && hour < 12;
    });
    const afternoon = slots.filter(s => {
      const hour = parseInt(s.start_time.split(':')[0]);
      return hour >= 12 && hour < 17;
    });
    const evening = slots.filter(s => {
      const hour = parseInt(s.start_time.split(':')[0]);
      return hour >= 17 && hour < 21;
    });
    
    return { morning, afternoon, evening };
  };
  
  const { morning, afternoon, evening } = groupSlotsByPeriod(daySlots);
  
  return (
    <Card className={`${className}`} data-testid="booking-slot-selector">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-indigo-600" />
          Select Date & Time
        </CardTitle>
        <CardDescription>
          Choose your preferred appointment slot
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Duration/Variant Selector */}
        {durationOptions.length > 1 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Service Duration</label>
            <Select
              value={String(currentDuration)}
              onValueChange={handleDurationChange}
              disabled={disabled}
            >
              <SelectTrigger data-testid="duration-select">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                {durationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                    {opt.priceImpact > 0 && (
                      <span className="text-green-600 ml-2">+₹{opt.priceImpact}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Service Info */}
        {availabilityData && (
          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{currentDuration} mins</span>
            </div>
            {availabilityData.buffer_time > 0 && (
              <div className="flex items-center gap-1">
                <span>+{availabilityData.buffer_time} min break</span>
              </div>
            )}
            {availabilityData.concurrent_sessions > 1 && (
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>{availabilityData.concurrent_sessions} concurrent slots</span>
              </div>
            )}
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              disabled={disabledDays}
              className="rounded-md border"
              data-testid="date-calendar"
            />
          </div>
          
          {/* Time Slots */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">
                Available Times
              </h4>
              <Badge variant="outline">
                {formatDate(selectedDate)}
              </Badge>
            </div>
            
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700">{error}</p>
                  <Button 
                    variant="link" 
                    className="text-red-600 p-0 h-auto"
                    onClick={fetchAvailability}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            ) : daySlots.length === 0 ? (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                <CalendarIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No available slots on this date</p>
                <p className="text-xs text-gray-400 mt-1">Try selecting a different date</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-80 overflow-y-auto">
                {/* Morning Slots */}
                {morning.length > 0 && (
                  <SlotGroup 
                    title="Morning" 
                    slots={morning} 
                    selectedTime={selectedTime}
                    onSelect={handleTimeSelect}
                    disabled={disabled}
                  />
                )}
                
                {/* Afternoon Slots */}
                {afternoon.length > 0 && (
                  <SlotGroup 
                    title="Afternoon" 
                    slots={afternoon} 
                    selectedTime={selectedTime}
                    onSelect={handleTimeSelect}
                    disabled={disabled}
                  />
                )}
                
                {/* Evening Slots */}
                {evening.length > 0 && (
                  <SlotGroup 
                    title="Evening" 
                    slots={evening} 
                    selectedTime={selectedTime}
                    onSelect={handleTimeSelect}
                    disabled={disabled}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Selected Slot Summary & Confirm */}
        {selectedTime && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div>
                  <p className="font-medium text-gray-900">
                    {formatDate(selectedDate)} at {formatTime(selectedTime)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {currentDuration} minutes session
                  </p>
                </div>
              </div>
              <Button 
                onClick={validateSlot}
                disabled={validating || disabled}
                data-testid="confirm-slot-btn"
              >
                {validating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Confirm Slot'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Slot Group Component
const SlotGroup = ({ title, slots, selectedTime, onSelect, disabled }) => {
  return (
    <div>
      <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
        {title}
      </h5>
      <div className="flex flex-wrap gap-2">
        {slots.map((slot) => (
          <button
            key={slot.start_time}
            onClick={() => onSelect(slot)}
            disabled={!slot.available || disabled}
            className={`
              px-3 py-1.5 text-sm rounded-md border transition-colors
              ${slot.available 
                ? selectedTime === slot.start_time
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }
            `}
            title={slot.available ? `${slot.remaining_capacity} spot${slot.remaining_capacity !== 1 ? 's' : ''} left` : slot.reason}
            data-testid={`slot-${slot.start_time}`}
          >
            {formatTime(slot.start_time)}
            {slot.available && slot.remaining_capacity < 3 && slot.remaining_capacity > 0 && (
              <span className="ml-1 text-xs opacity-75">({slot.remaining_capacity})</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// Utility functions
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export default BookingSlotSelector;
